import React, { useEffect, useState, useRef } from "react";
import {
  initialNodeStyle,
  initialUndirectedWeightedEdgeStyle,
  visitedNodeStyle,
  visitedUndirectedWeightedEdgeStyle,
  visitingNodeStyle,
} from "./GraphStyle";
import "./BFSAlgorithm.css";
import "./Prims.css";

const Kruskals = ({ cytoscape, nodes }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationCompleted, setAnimationCompleted] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [allEdges, setAllEdges] = useState([]);
  const [minimumSpanningEdges, setMinimumSpanningEdges] = useState([]);
  const setsRef = useRef([]);

  const animateKruskals = async () => {
    const sets = [];
    for (const node of nodes) {
      sets.push(new Set([node.data.id]));
    }
    setsRef.current = [...sets];
    const edges = cytoscape.edges();
    const sortedEdges = edges.sort((edge1, edge2) => {
      const weight1 = edge1.data("weight");
      const weight2 = edge2.data("weight");
      return weight1 - weight2;
    });
    setAllEdges(sortedEdges);
    const minimumSpanningEdges = [];

    for (const edge of sortedEdges) {
      const source = edge.data("source");
      const target = edge.data("target");

      const sourceSetIndex = findNodeSet(sets, source);
      const targetSetIndex = findNodeSet(sets, target);

      if (sourceSetIndex !== targetSetIndex) {
        minimumSpanningEdges.push(edge);
        sets[sourceSetIndex] = new Set([
          ...sets[sourceSetIndex],
          ...sets[targetSetIndex],
        ]);
        sets.splice(targetSetIndex, 1);
        // Color source and target nodes as visiting nodes
        cytoscape
          .nodes(`[id='${source}'], [id='${target}']`)
          .style(visitedNodeStyle);
        // Color selected edge as visited edge
        edge.style(visitedUndirectedWeightedEdgeStyle);
        setSelectedEdge(edge);
        setMinimumSpanningEdges([...minimumSpanningEdges]);

        await new Promise((resolve) => setTimeout(resolve, 4000)); // Delay for visualization
      }
    }
    setIsAnimating(false);
    setAnimationCompleted(true);
  };

  const findNodeSet = (sets, node) => {
    return sets.findIndex((set) => set.has(node));
  };

  const resetAnimation = () => {
    if (isAnimating) return;

    cytoscape.elements("node").style(initialNodeStyle);
    cytoscape.elements("edge").style(initialUndirectedWeightedEdgeStyle);

    setIsAnimating(false);
    setAnimationCompleted(false);
    setSelectedEdge(null);
    setMinimumSpanningEdges([]);
  };

  const startAnimation = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      animateKruskals();
    }
  };

  return (
    <div>
      <div className="button-container">
        <button
          className="animate-reset-button"
          onClick={startAnimation}
          disabled={isAnimating || animationCompleted}
        >
          {isAnimating ? "Animating..." : "Animate Kruskal's"}
        </button>
        &nbsp;&nbsp;
        <button
          className="animate-reset-button"
          onClick={resetAnimation}
          disabled={isAnimating}
        >
          Reset Animation
        </button>
      </div>
      <div className="table-container">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Target</th>
              <th>Weight</th>
            </tr>
          </thead>
          <tbody>
            {allEdges.map((edge, index) => (
              <tr
                key={index}
                className={
                  minimumSpanningEdges.includes(edge) ? "minimum-spanning" : ""
                }
              >
                <td>{edge.data("source")}</td>
                <td>{edge.data("target")}</td>
                <td>{edge.data("weight")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <div className="stack-container">
          <div className="stack-title">Minimum Spanning Edges:</div>
          <ul className="stack">
            {minimumSpanningEdges.map((edge, index) => (
              <li key={index}>{`${edge.data("source")} ➔ ${edge.data(
                "target"
              )}`}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Kruskals;
